import {
  postTypeIconClassName,
  postTypeIconLabel,
} from "@/features/post/post-type-icon";
import React, { useState } from "react";
import PropTypes from "prop-types";
import times from "lodash/times";

import SkedIcon from "ui/SkedIcon/SkedIcon";
import { POST_TYPES } from "constants/PostsView";
import { PAGES } from "constants/Pages";

import NoMediaIcon from "assets/icons/no-media.svg";

import { resolveImgThumbnail } from "../../../../utils/posts";
import { convertCustomerUploadsS3ToCloudFront } from "libs/media-files";
function PostMedia({ post, page, accounts }) {
  let isInstagram = false;
  if (accounts && accounts.length > 0) {
    isInstagram = accounts.some((account) => {
      return account.platformType === "IG" || !account.platformType;
    });
  }
  let isFacebook = false;
  if (accounts && accounts.length > 0) {
    isFacebook = accounts.some((account) => {
      return (
        account.platformType === "FB" || account.platformName === "facebook"
      );
    });
  }

  const [mediaIndex, setMediaIndex] = useState(0);
  if (post.type === POST_TYPES.TEXT) {
    return (
      <div className="relative flex flex-col items-center justify-center p-4 bg-neutral-50 rounded-lg min-h-[120px]">
        <div className="flex flex-col items-center gap-2">
          <span className="text-neutral-400">
            <NoMediaIcon />
          </span>
          <span className="text-xs text-neutral-500 font-medium">
            Text Post
          </span>
        </div>
        {page === PAGES.UPCOMING && <PostStatusTag post={post} />}
      </div>
    );
  }

  const media = getMedia(post);

  if (!media) {
    return null;
  }

  const mediaObj = media[mediaIndex];
  if (!mediaObj) return null;
  // const mediaSrc = mediaObj.thumbnailUrl || mediaObj.url;
  return (
    <div className="flex flex-col">
      <MediaMiddle
        post={post}
        mediaObj={mediaObj}
        mediaLength={media.length}
        mediaIndex={mediaIndex}
        setMediaIndex={setMediaIndex}
        useThumbnail={true}
        isInstagram={isInstagram}
        isFacebook={isFacebook}
        videoPostType={post.videoPostType}
        page={page}
      />
      <MediaBottom
        post={post}
        mediaLength={media.length}
        mediaIndex={mediaIndex}
        setMediaIndex={setMediaIndex}
      />
    </div>
  );
}

PostMedia.propTypes = {
  post: PropTypes.object,
  page: PropTypes.string,
  accounts: PropTypes.array,
};

function MediaMiddle(props) {
  const {
    post,
    isInstagram,
    isFacebook,
    videoPostType,
    mediaObj,
    mediaLength,
    mediaIndex,
    setMediaIndex,
    useThumbnail,
    page,
  } = props;

  return (
    <div className="relative rounded-lg overflow-hidden group border border-neutral-200">
      {(useThumbnail || mediaObj.isVideo) && (
        <a href={mediaObj.url} target="_blank" rel="noopener noreferrer">
          <img
            src={mediaObj.thumbnailUrl}
            alt="Thumbnail of post"
            className="w-full aspect-square object-cover group-hover:scale-105 transition-all duration-200"
          />
        </a>
      )}
      {!useThumbnail && !mediaObj.isVideo && (
        <img
          src={`${mediaObj.url}?nocache=true`}
          alt="Image of post"
          crossOrigin="anonymous"
          className="w-full aspect-square object-cover group-hover:scale-105 transition-all duration-200"
        />
      )}
      {mediaObj.isVideo && (
        <a href={mediaObj.url} target="_blank" rel="noopener noreferrer">
          <PostTypeIcon
            post={post}
            isInstagram={isInstagram}
            isFacebook={isFacebook}
            videoPostType={videoPostType}
          />
        </a>
      )}
      {!mediaObj.isVideo && <PostTypeIcon post={post} />}
      {page === PAGES.UPCOMING && <PostStatusTag post={post} />}
      {/* Carousel indicator badge */}
      {mediaLength > 1 && (
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded backdrop-blur-sm">
          {mediaIndex + 1}/{mediaLength}
        </div>
      )}
      {mediaLength > 1 && (
        <button
          className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
          type="button"
          onClick={() => setMediaIndex(mediaIndex - 1)}
          disabled={mediaIndex === 0}
          aria-label={`Previous image, ${mediaIndex + 1} of ${mediaLength}`}
        >
          <i
            className="fa-solid fa-chevron-left text-neutral-700 text-sm"
            aria-hidden="true"
          ></i>
        </button>
      )}

      {mediaLength > 1 && (
        <button
          className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
          type="button"
          onClick={() => setMediaIndex(mediaIndex + 1)}
          disabled={mediaIndex === mediaLength - 1}
          aria-label={`Next image, ${mediaIndex + 1} of ${mediaLength}`}
        >
          <i
            className="fa-solid fa-chevron-right text-neutral-700 text-sm"
            aria-hidden="true"
          ></i>
        </button>
      )}
    </div>
  );
}

MediaMiddle.propTypes = {
  post: PropTypes.object,
  isInstagram: PropTypes.bool,
  isFacebook: PropTypes.bool,
  videoPostType: PropTypes.string,
  mediaObj: PropTypes.object,
  mediaLength: PropTypes.number,
  mediaIndex: PropTypes.number,
  setMediaIndex: PropTypes.func,
  useThumbnail: PropTypes.bool,
  page: PropTypes.string,
};

function MediaBottom({ post, mediaLength, mediaIndex, setMediaIndex }) {
  return (
    <div className="flex justify-center items-center py-2">
      {mediaLength > 1 && (
        <div className="flex gap-1.5 flex-wrap justify-center max-w-full">
          {times(mediaLength, (index) => {
            return (
              <button
                key={`dots-${post._id}-${index}`}
                onClick={() => setMediaIndex(index)}
                className={`h-1.5 w-1.5 rounded-full transition-all duration-200 ${
                  index === mediaIndex ? "bg-neutral-700" : "bg-neutral-300"
                }`}
                aria-label={`View image ${index + 1} of ${mediaLength}`}
                aria-current={index === mediaIndex ? "true" : "false"}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

MediaBottom.propTypes = {
  post: PropTypes.object,
  mediaLength: PropTypes.number,
  mediaIndex: PropTypes.number,
  setMediaIndex: PropTypes.func,
};

function PostTypeIcon({ post }) {
  return (
    <SkedIcon
      icon={postTypeIconClassName(post)}
      tooltip={postTypeIconLabel(post)}
      placement="top"
      className="PostMedia-MediaTypeIcon absolute top-2 right-2 z-10"
      size="20"
    />
  );
}

PostTypeIcon.propTypes = {
  post: PropTypes.object,
  isInstagram: PropTypes.bool,
  isFacebook: PropTypes.bool,
  videoPostType: PropTypes.string,
};

function PostStatusTag({ post }) {
  const queued = post.queued === true;
  const status = queued
    ? { label: "Q", tooltip: "Queued", color: "rgb(32, 20, 102)" }
    : { label: "S", tooltip: "Scheduled", color: "rgb(79, 49, 255)" };

  return (
    <div
      className="group/badge absolute bottom-1 left-1 h-5 min-w-[1.25rem] rounded text-white text-xs font-bold flex items-center justify-center shadow transition-all duration-200 ease-in-out hover:px-2 z-10"
      style={{ backgroundColor: status.color }}
    >
      <span className="group-hover/badge:hidden">{status.label}</span>
      <span className="hidden group-hover/badge:inline whitespace-nowrap">
        {status.tooltip}
      </span>
    </div>
  );
}

PostStatusTag.propTypes = {
  post: PropTypes.object,
};

export function getMedia(post) {
  const { altText, _id } = post;
  let { originalUrl, url, thumbnailUrl } = post;

  const filesCount = post.mediaFiles && post.mediaFiles.length > 0;
  const newAltText =
    (filesCount === 1 && post.mediaFiles[0]?.altText) || altText;
  let imgThumbnail = resolveImgThumbnail(post);
  //safety check to convert s3 bucket urls to cloudfront urls
  originalUrl = convertCustomerUploadsS3ToCloudFront(originalUrl);
  url = convertCustomerUploadsS3ToCloudFront(url);
  thumbnailUrl = convertCustomerUploadsS3ToCloudFront(thumbnailUrl);
  imgThumbnail = convertCustomerUploadsS3ToCloudFront(imgThumbnail);

  switch (post.type) {
    case POST_TYPES.MULTI_MEDIA:
    case POST_TYPES.STORY: {
      return filesCount
        ? post.mediaFiles
        : [
            {
              _id,
              altText: newAltText,
              originalUrl,
              thumbnailUrl,
              url,
              isVideo: post.isVideo,
              meta: post.meta || {},
            },
          ];
    }
    case POST_TYPES.PHOTO:
    case POST_TYPES.IMAGE:
    case POST_TYPES.VIDEO:
    case POST_TYPES.FB_REEL:
    case POST_TYPES.DOCUMENT: {
      return [
        {
          _id,
          altText: newAltText,
          originalUrl,
          thumbnailUrl: imgThumbnail,
          url,
          isVideo: post.isVideo,
          meta: post.meta || {},
        },
      ];
    }
    default: {
      console.error(`Unhandled post type ${post.type}`);
    }
  }
}

export default PostMedia;
